import { useRouter } from 'vue-router';

import { t } from '@/i18n';
import type { DataDispatcher } from '@/modules/common/state/types';
import type { UsePageControllerResult } from '@/modules/common/state/usePageController';
import { usePageSubmit } from '@/modules/common/state/usePageSubmit';

import type { ConditionRow } from '../../../../api/blacklistAutoRuleContract';
import { toBlacklistPayload } from '../../../../api/blacklistAutoRuleContract';
import { readBlacklistErrorMessage } from '../../../../utils/readBlacklistErrorMessage';
import type { BlacklistEditorState } from '../state/useBlacklistEditorState';

type UseBlacklistRuleSaveOptions = Readonly<{
  page: UsePageControllerResult;
  state: BlacklistEditorState;
  back: () => Promise<unknown>;
  isCancelled: () => boolean;
}>;

export const useBlacklistRuleSave = ({
  page,
  state,
  back,
  isCancelled,
}: UseBlacklistRuleSaveOptions): DataDispatcher => {
  const router = useRouter();
  const { form, conditions, queries } = state;

  const showServerError = (error: unknown, isStale: () => boolean): void => {
    if (isStale()) {
      return;
    }
    page.errors.showErrorItem({
      description: readBlacklistErrorMessage(error) ?? t('modules.blacklistAutoRules.editor.saveFailed'),
    });
  };

  // The rows to send, or null with the reason shown above the conditions table.
  const readValidConditions = (): ReadonlyArray<ConditionRow> | null => {
    const result = conditions.check();
    if (result.error !== null) {
      state.conditionError.value = result.error;
    }
    return result.rows;
  };

  return usePageSubmit({
    pageController: page,
    isDirty: state.isDirty,
    unexpectedTitle: () => t('modules.blacklistAutoRules.editor.saveFailed'),
    run: async () => {
      if (!state.ready.value) {
        return false;
      }
      const requestRoute = router.currentRoute.value;
      const isStale = () => isCancelled() || router.currentRoute.value !== requestRoute;
      state.conditionError.value = '';
      state.nameError.value = '';
      const values = await form.actions.submit();
      if (isStale()) {
        return false;
      }
      if (values === false && !state.nameSchema.safeParse(form.state.formData.value.name).success) {
        state.nameError.value = t('modules.blacklistAutoRules.editor.nameRequired');
      }
      const validRows = readValidConditions();
      if (values === false || validRows === null) {
        return false;
      }
      try {
        await queries.save(toBlacklistPayload(values, validRows));
      } catch (error) {
        showServerError(error, isStale);
        return false;
      }
      // The user left while the save was in flight: the rule is saved, but the page is gone —
      // navigating now would pull them away from wherever they went.
      if (isStale()) {
        return true;
      }
      await form.actions.afterSubmit?.();
      if (isStale()) {
        return true;
      }
      state.seedConditions(conditions.table.rows.value);
      page.notifications.showNotification(t('modules.blacklistAutoRules.editor.saved'));
      await back();
      return true;
    },
  });
};
